import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Key, Eye, EyeOff, Link, UserPlus, Unlink, Plane, Calendar, Mail, CheckCircle, AlertCircle, Send, Pencil, KeyRound } from 'lucide-react';

const Users = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState({ open: false, user: null });
  const [linkBarberDialog, setLinkBarberDialog] = useState({ open: false, user: null });
  const [emailDialog, setEmailDialog] = useState({ open: false, user: null });
  const [contactEmail, setContactEmail] = useState('');
  const [sendingVerification, setSendingVerification] = useState(false);
  const [vacationDialog, setVacationDialog] = useState({ open: false, user: null });
  const [vacationData, setVacationData] = useState({
    is_on_vacation: false,
    vacation_start: '',
    vacation_end: '',
    vacation_note: ''
  });
  const [selectedBarberId, setSelectedBarberId] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'barber',
    barber_id: '',
    contact_email: ''
  });

  useEffect(() => {
    if (isAdmin()) {
      fetchUsers();
      fetchBarbers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (error) {
      toast.error('فشل تحميل المستخدمين');
    } finally {
      setLoading(false);
    }
  };

  const fetchBarbers = async () => {
    try {
      const res = await api.get('/barbers');
      setBarbers(res.data);
    } catch (error) {
      console.error('Failed to fetch barbers');
    }
  };

  // Get unlinked barbers (not connected to any user)
  const getUnlinkedBarbers = () => {
    const linkedBarberIds = users
      .filter(u => u.barber_id)
      .map(u => u.barber_id);
    const linkedByUserId = barbers
      .filter(b => b.user_id)
      .map(b => b.id);
    const allLinked = [...new Set([...linkedBarberIds, ...linkedByUserId])];
    return barbers.filter(b => !allLinked.includes(b.id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If role is barber, must select a barber to link
    if (formData.role === 'barber' && !formData.barber_id) {
      toast.error('يجب اختيار حلاق لربطه بهذا الحساب');
      return;
    }
    
    try {
      await api.post('/auth/register', formData);
      toast.success('تم إضافة المستخدم بنجاح');
      setOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'barber', barber_id: '' });
      fetchUsers();
      fetchBarbers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إضافة المستخدم');
    }
  };

  const handleLinkBarber = async () => {
    if (!selectedBarberId) {
      toast.error('يجب اختيار حلاق');
      return;
    }
    try {
      await api.post('/admin/link-user-barber', {
        user_id: linkBarberDialog.user.id,
        barber_id: selectedBarberId
      });
      toast.success('تم ربط المستخدم بالحلاق بنجاح');
      setLinkBarberDialog({ open: false, user: null });
      setSelectedBarberId('');
      fetchUsers();
      fetchBarbers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل ربط المستخدم بالحلاق');
    }
  };

  const handleUnlinkBarber = async (userId) => {
    if (!window.confirm('هل أنت متأكد من فك ربط هذا المستخدم بالحلاق؟')) return;
    try {
      await api.post(`/admin/unlink-user-barber/${userId}`);
      toast.success('تم فك الربط بنجاح');
      fetchUsers();
      fetchBarbers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل فك الربط');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('تم حذف المستخدم بنجاح');
      fetchUsers();
    } catch (error) {
      toast.error('فشل حذف المستخدم');
    }
  };

  const handleChangeUserPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 4) {
      toast.error('كلمة المرور يجب أن تكون 4 أحرف على الأقل');
      return;
    }
    try {
      await api.post(`/admin/change-user-password`, {
        user_id: passwordDialog.user.id,
        new_password: newPassword
      });
      toast.success(`تم تغيير كلمة مرور ${passwordDialog.user.name} بنجاح`);
      setPasswordDialog({ open: false, user: null });
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تغيير كلمة المرور');
    }
  };

  const getLinkedBarber = (user) => {
    if (user.barber_id) {
      return barbers.find(b => b.id === user.barber_id);
    }
    return barbers.find(b => b.user_id === user.id);
  };

  const openVacationDialog = (user) => {
    setVacationData({
      is_on_vacation: user.is_on_vacation || false,
      vacation_start: user.vacation_start || '',
      vacation_end: user.vacation_end || '',
      vacation_note: user.vacation_note || ''
    });
    setVacationDialog({ open: true, user });
  };

  const handleUpdateVacation = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${vacationDialog.user.id}/vacation`, vacationData);
      toast.success(vacationData.is_on_vacation ? 'تم تفعيل الإجازة بنجاح' : 'تم إنهاء الإجازة بنجاح');
      setVacationDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحديث حالة الإجازة');
    }
  };

  // Email Management Functions
  const openEmailDialog = (user) => {
    setContactEmail(user.contact_email || '');
    setEmailDialog({ open: true, user });
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${emailDialog.user.id}`, {
        contact_email: contactEmail
      });
      toast.success('تم تحديث البريد الإلكتروني بنجاح');
      setEmailDialog({ open: false, user: null });
      setContactEmail('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحديث البريد الإلكتروني');
    }
  };

  const handleSendVerification = async (userId) => {
    setSendingVerification(true);
    try {
      await api.post(`/email-verification/send/${userId}`);
      toast.success('تم إرسال رابط التحقق إلى البريد الإلكتروني');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال رابط التحقق');
    } finally {
      setSendingVerification(false);
    }
  };

  const handleSendPasswordReset = async (user) => {
    if (!user.contact_email) {
      toast.error('يجب إضافة بريد إلكتروني للمستخدم أولاً');
      return;
    }
    if (!window.confirm(`هل تريد إرسال رابط إعادة تعيين كلمة المرور إلى ${user.name} على البريد ${user.contact_email}؟`)) {
      return;
    }
    try {
      const res = await api.post(`/users/${user.id}/send-password-reset`);
      toast.success(res.data?.message || 'تم إرسال الرابط');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إرسال الرابط');
    }
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('users')}</h1>
          <p className="text-muted-foreground">إدارة حسابات المستخدمين</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="bg-primary text-primary-foreground">
              <Plus className="w-4 h-4 me-2" />
              {t('addUser')}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle>{t('addUser')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">{t('name')}</label>
                <Input
                  data-testid="user-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-secondary/50 border-white/10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('email')}</label>
                <Input
                  data-testid="user-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-secondary/50 border-white/10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('password')}</label>
                <Input
                  data-testid="user-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="bg-secondary/50 border-white/10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('role')}</label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value, barber_id: '' })}>
                  <SelectTrigger data-testid="user-role-select" className="bg-secondary/50 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    <SelectItem value="admin">{t('admin')}</SelectItem>
                    <SelectItem value="barber">{t('barber')}</SelectItem>
                    <SelectItem value="guest">{t('customer')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Contact Email Field */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Mail className="w-4 h-4 inline me-1" />
                  البريد الإلكتروني الفعلي (للاستعادة)
                </label>
                <Input
                  data-testid="user-contact-email-input"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="example@gmail.com"
                  className="bg-secondary/50 border-white/10"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  سيتم استخدامه لاستعادة كلمة المرور
                </p>
              </div>
              
              {/* Show barber selection when role is barber */}
              {formData.role === 'barber' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Link className="w-4 h-4 inline me-1" />
                    ربط بحلاق
                  </label>
                  {getUnlinkedBarbers().length > 0 ? (
                    <Select value={formData.barber_id} onValueChange={(value) => setFormData({ ...formData, barber_id: value })}>
                      <SelectTrigger data-testid="barber-link-select" className="bg-secondary/50 border-white/10">
                        <SelectValue placeholder="اختر الحلاق للربط" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-white/10">
                        {getUnlinkedBarbers().map(barber => (
                          <SelectItem key={barber.id} value={barber.id}>
                            {barber.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-sm text-yellow-500">
                      لا يوجد حلاقين غير مرتبطين. أضف حلاق جديد من صفحة الحلاقين أولاً.
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    يجب ربط المستخدم بحلاق موجود في صفحة الحلاقين
                  </p>
                </div>
              )}
              
              <Button data-testid="submit-user-btn" type="submit" className="w-full bg-primary text-primary-foreground">
                <UserPlus className="w-4 h-4 me-2" />
                {t('save')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50 border-b border-white/5">
              <tr>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('name')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">اسم المستخدم</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">البريد الإلكتروني</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('role')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الحالة</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الحلاق المرتبط</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const linkedBarber = getLinkedBarber(user);
                return (
                <tr key={user.id} className="border-b border-white/5 hover:bg-secondary/20">
                  <td className="px-6 py-4">{user.name}</td>
                  <td className="px-6 py-4 text-muted-foreground">{user.email}</td>
                  <td className="px-6 py-4">
                    {user.contact_email ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{user.contact_email}</span>
                        {user.email_verified ? (
                          <span className="flex items-center gap-1 text-green-400" title="تم التحقق">
                            <CheckCircle className="w-4 h-4" />
                          </span>
                        ) : (
                          <Button
                            data-testid={`send-verification-${user.id}`}
                            onClick={() => handleSendVerification(user.id)}
                            variant="ghost"
                            size="sm"
                            className="text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10 p-1 h-auto"
                            title="إرسال رابط التحقق"
                            disabled={sendingVerification}
                          >
                            <Send className="w-3 h-3" />
                          </Button>
                        )}
                        <Button
                          data-testid={`edit-email-${user.id}`}
                          onClick={() => openEmailDialog(user)}
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-primary p-1 h-auto"
                          title="تعديل البريد الإلكتروني"
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        data-testid={`add-email-${user.id}`}
                        onClick={() => openEmailDialog(user)}
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-primary"
                      >
                        <Mail className="w-4 h-4 me-1" />
                        إضافة بريد
                      </Button>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      user.role === 'admin' ? 'bg-primary/20 text-primary' :
                      user.role === 'barber' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {t(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {user.is_on_vacation ? (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400 flex items-center gap-1 w-fit">
                        <Plane className="w-3 h-3" />
                        في إجازة
                        {user.vacation_end && <span className="text-[10px]">حتى {user.vacation_end}</span>}
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                        نشط
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {linkedBarber ? (
                      <div className="flex items-center gap-2">
                        <span className="text-green-400 flex items-center gap-1">
                          <Link className="w-3 h-3" />
                          {linkedBarber.name}
                        </span>
                        <Button
                          onClick={() => handleUnlinkBarber(user.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-1 h-auto"
                          title="فك الربط"
                        >
                          <Unlink className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : user.role === 'barber' ? (
                      <Button
                        onClick={() => setLinkBarberDialog({ open: true, user })}
                        variant="ghost"
                        size="sm"
                        className="text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10"
                      >
                        <Link className="w-4 h-4 me-1" />
                        ربط بحلاق
                      </Button>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Button
                        data-testid={`vacation-${user.id}`}
                        onClick={() => openVacationDialog(user)}
                        variant="ghost"
                        size="sm"
                        className={`hover:bg-orange-500/10 ${user.is_on_vacation ? 'text-orange-400' : 'text-muted-foreground hover:text-orange-400'}`}
                        title="إدارة الإجازة"
                      >
                        <Plane className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`change-password-${user.id}`}
                        onClick={() => setPasswordDialog({ open: true, user })}
                        variant="ghost"
                        size="sm"
                        className="text-primary hover:text-primary/80 hover:bg-primary/10"
                        title="تغيير كلمة المرور (مباشر)"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`send-password-reset-${user.id}`}
                        onClick={() => handleSendPasswordReset(user)}
                        variant="ghost"
                        size="sm"
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 disabled:opacity-40"
                        title={user.contact_email ? `إرسال رابط إعادة تعيين كلمة المرور إلى ${user.contact_email}` : 'يجب إضافة بريد إلكتروني أولاً'}
                        disabled={!user.contact_email}
                      >
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`delete-user-${user.id}`}
                        onClick={() => handleDelete(user.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        title="حذف المستخدم"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialog.open} onOpenChange={(open) => {
        setPasswordDialog({ open, user: open ? passwordDialog.user : null });
        if (!open) setNewPassword('');
      }}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-primary" />
              تغيير كلمة مرور {passwordDialog.user?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangeUserPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">كلمة المرور الجديدة</label>
              <div className="relative">
                <Input
                  data-testid="new-user-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="أدخل كلمة المرور الجديدة"
                  required
                  minLength={4}
                  className="bg-secondary/50 border-white/10 pe-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">يجب أن تكون 4 أحرف على الأقل</p>
            </div>
            <div className="flex gap-3">
              <Button 
                type="submit" 
                className="flex-1 bg-primary text-primary-foreground"
                data-testid="submit-change-password-btn"
              >
                <Key className="w-4 h-4 me-2" />
                تغيير كلمة المرور
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setPasswordDialog({ open: false, user: null })}
                className="border-white/10"
              >
                إلغاء
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Link Barber Dialog */}
      <Dialog open={linkBarberDialog.open} onOpenChange={(open) => {
        setLinkBarberDialog({ open, user: open ? linkBarberDialog.user : null });
        if (!open) setSelectedBarberId('');
      }}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="w-5 h-5 text-primary" />
              ربط {linkBarberDialog.user?.name} بحلاق
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {getUnlinkedBarbers().length > 0 ? (
              <>
                <Select value={selectedBarberId} onValueChange={setSelectedBarberId}>
                  <SelectTrigger className="bg-secondary/50 border-white/10">
                    <SelectValue placeholder="اختر الحلاق" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {getUnlinkedBarbers().map(barber => (
                      <SelectItem key={barber.id} value={barber.id}>
                        {barber.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-3">
                  <Button 
                    onClick={handleLinkBarber}
                    className="flex-1 bg-primary text-primary-foreground"
                    disabled={!selectedBarberId}
                  >
                    <Link className="w-4 h-4 me-2" />
                    ربط
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setLinkBarberDialog({ open: false, user: null })}
                    className="border-white/10"
                  >
                    إلغاء
                  </Button>
                </div>
              </>
            ) : (
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 text-center">
                <p className="text-yellow-500">لا يوجد حلاقين غير مرتبطين</p>
                <p className="text-sm text-muted-foreground mt-1">أضف حلاق جديد من صفحة الحلاقين أولاً</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Vacation Dialog */}
      <Dialog open={vacationDialog.open} onOpenChange={(open) => {
        setVacationDialog({ open, user: open ? vacationDialog.user : null });
      }}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plane className="w-5 h-5 text-orange-400" />
              إدارة إجازة {vacationDialog.user?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateVacation} className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
              <label className="font-medium">في إجازة</label>
              <button
                type="button"
                onClick={() => setVacationData(prev => ({ ...prev, is_on_vacation: !prev.is_on_vacation }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  vacationData.is_on_vacation ? 'bg-orange-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    vacationData.is_on_vacation ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {vacationData.is_on_vacation && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Calendar className="w-4 h-4 inline-block me-1" />
                    تاريخ بداية الإجازة
                  </label>
                  <Input
                    type="date"
                    value={vacationData.vacation_start}
                    onChange={(e) => setVacationData(prev => ({ ...prev, vacation_start: e.target.value }))}
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <Calendar className="w-4 h-4 inline-block me-1" />
                    تاريخ نهاية الإجازة (العودة المتوقعة)
                  </label>
                  <Input
                    type="date"
                    value={vacationData.vacation_end}
                    onChange={(e) => setVacationData(prev => ({ ...prev, vacation_end: e.target.value }))}
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">ملاحظة (اختياري)</label>
                  <Input
                    type="text"
                    value={vacationData.vacation_note}
                    onChange={(e) => setVacationData(prev => ({ ...prev, vacation_note: e.target.value }))}
                    placeholder="مثال: إجازة مرضية، إجازة سنوية..."
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
              </>
            )}
            
            <div className="flex gap-3">
              <Button 
                type="submit"
                className={`flex-1 ${vacationData.is_on_vacation ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-500 hover:bg-green-600'} text-white`}
              >
                {vacationData.is_on_vacation ? (
                  <>
                    <Plane className="w-4 h-4 me-2" />
                    تفعيل الإجازة
                  </>
                ) : (
                  'إنهاء الإجازة'
                )}
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setVacationDialog({ open: false, user: null })}
                className="border-white/10"
              >
                إلغاء
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialog.open} onOpenChange={(open) => !open && setEmailDialog({ open: false, user: null })}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              تعديل البريد الإلكتروني - {emailDialog.user?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateEmail} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">البريد الإلكتروني الفعلي</label>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="example@gmail.com"
                required
                className="bg-secondary/50 border-white/10"
                dir="ltr"
              />
              <p className="text-xs text-muted-foreground mt-2">
                سيتم استخدام هذا البريد لاستعادة كلمة المرور وإرسال الإشعارات
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-primary text-primary-foreground">
                <Mail className="w-4 h-4 me-2" />
                حفظ
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setEmailDialog({ open: false, user: null })}
                className="border-white/10"
              >
                إلغاء
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;